/**
 * `<RankDeltaBadge>` — colored ↑/↓/= chip showing rank movement (B-13).
 *
 * HoD Q3 ruling: inline in `<VerdictCard>` header via `trailingBadge` slot.
 * Chip format: `↑ 2 (#5→#3)` (terse, visible text).
 * Full descriptive text `↑ Moved up 2 spots · #5→#3` as:
 *   - `aria-label` (screen readers — condition #4)
 *   - `title` (tooltip on hover — condition #4)
 *
 * Visual:
 *   ↑ N (#B→#A)  → green (#10B981) — rank improved (rankAfter < rankBefore)
 *   ↓ N (#A→#B)  → red   (#EF4444) — rank declined
 *   = (#A)       → slate (#94A3B8) — no change
 *
 * When either value is null/undefined, renders nothing.
 */
import type { JSX } from "react";
import {
  VERDICT_NULL_COLOR,
  VERDICT_COLORS,
  SECTION_COLORS,
} from "@/lib/tokens";

interface RankDeltaBadgeProps {
  /** Rank position before the analysis was generated (lower = better). */
  rankBefore: number | null | undefined;
  /** Rank position after the analysis. */
  rankAfter: number | null | undefined;
}

export function RankDeltaBadge({
  rankBefore,
  rankAfter,
}: RankDeltaBadgeProps): JSX.Element | null {
  if (rankBefore == null || rankAfter == null) return null;

  const delta = rankBefore - rankAfter; // positive = improved (moved up)

  if (delta === 0) {
    const ariaText = `= Rank unchanged · #${rankBefore}`;
    return (
      <span
        data-testid="rank-delta-badge"
        data-delta="same"
        className="inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-bold"
        style={{ color: VERDICT_NULL_COLOR, background: `${VERDICT_NULL_COLOR}14`, border: `1px solid ${VERDICT_NULL_COLOR}33` }}
        aria-label={ariaText}
        title={ariaText}
      >
        = (#{rankBefore})
      </span>
    );
  }

  if (delta > 0) {
    // Improved — moved up in rankings.
    const ariaText = `↑ Moved up ${delta} spot${delta === 1 ? "" : "s"} · #${rankBefore}→#${rankAfter}`;
    return (
      <span
        data-testid="rank-delta-badge"
        data-delta="up"
        className="inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-bold"
        style={{ color: SECTION_COLORS.strengths, background: `${SECTION_COLORS.strengths}14`, border: `1px solid ${SECTION_COLORS.strengths}33` }}
        aria-label={ariaText}
        title={ariaText}
      >
        ↑ {delta} (#{rankBefore}→#{rankAfter})
      </span>
    );
  }

  // Declined.
  const absDelta = Math.abs(delta);
  const ariaText = `↓ Moved down ${absDelta} spot${absDelta === 1 ? "" : "s"} · #${rankBefore}→#${rankAfter}`;
  return (
    <span
      data-testid="rank-delta-badge"
      data-delta="down"
      className="inline-flex items-center rounded px-1.5 py-0.5 text-2xs font-bold"
      style={{ color: VERDICT_COLORS.GAP, background: `${VERDICT_COLORS.GAP}14`, border: `1px solid ${VERDICT_COLORS.GAP}33` }}
      aria-label={ariaText}
      title={ariaText}
    >
      ↓ {absDelta} (#{rankBefore}→#{rankAfter})
    </span>
  );
}
